// client/src/components/layout/MainLayout.jsx
// Layout principal v2 « Arcane Nocturne » : brand + navigation, monnaies,
// wallet, profil. Toute la logique R34f/R34h (refresh wallet, duel en cours,
// tutorial) est conservée à l'identique.

import React, { useEffect, useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useWallet } from '../../contexts/WalletContext';
import { CoinLogo, OrinLogo, OriLogo } from '../ui/CurrencyLogos';
import { getCoinBalance } from '../../api/shopService';
import { useNavigate } from 'react-router-dom';
import gameService from '../../api/gameService';
import Icon from '../ui/Icon';
import './MainLayout.css';
import TutorialTour from '../tutorial/TutorialTour';
import { shouldLaunchTutorial } from '../../services/tutorialService';
import { lazy, Suspense } from 'react';
const OrinBuyModal = lazy(() => import('../wallet/OrinBuyModal'));
const WalletModal = lazy(() => import('../wallet/WalletModal'));

const NAV_LINKS = [
  { to: '/game/matchmaking', label: 'Duel', icon: 'play', end: false },
  { to: '/collection', label: 'Collection', icon: 'collection', end: false },
  { to: '/campaign', label: 'Campagne', icon: 'sparkles', end: false },
  { to: '/shop', label: 'Boutique', icon: 'shop', end: false },
  { to: '/marketplace', label: 'Marché', icon: 'coins', end: false },
  { to: '/rankings', label: 'Classement', icon: 'rankings', end: false },
];

const MainLayout = () => {
  const { isAuthenticated, currentUser } = useAuth();
  const { connected: walletConn } = useWallet();
  const [tutorialAutoLaunch] = useState(() => shouldLaunchTutorial(currentUser));
  const location = useLocation();
  const [wallet, setWallet] = useState(null);
  const [activeMatch, setActiveMatch] = useState(null);
  const [showOrinBuy, setShowOrinBuy] = useState(false); // { gameId } si un duel est en cours
  const [showWallet, setShowWallet] = useState(false);
  const navigate = useNavigate();

  const shortAddr = (a) => a && (a.slice(0, 4) + '…' + a.slice(-4));

  // Ne pas afficher le header sur la page d'accueil (EntryScreen)
  const isHomePage = location.pathname === '/';

  const isActive = (to) => {
    if (to === '/collection') return location.pathname.startsWith('/collection') || location.pathname.startsWith('/decks');
    return location.pathname.startsWith(to);
  };

  // R34f4 : portefeuille complet — rafraîchi à chaque navigation, toutes les
  // 30 s, et sur l'événement global 'wallet:refresh' (émis après un claim).
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    let alive = true;
    const load = () => {
      getCoinBalance()
        .then((data) => {
          if (alive && data && typeof data.coins === 'number') {
            setWallet({ coins: data.coins, orin: data.orin || 0, ori: data.ori || 0 });
          }
        })
        .catch(() => {});
    };
    load();
    const onRefresh = () => load();
    const timer = setInterval(load, 30000);
    window.addEventListener('wallet:refresh', onRefresh);

    // R34h : détecter un duel en cours (hors écran de jeu) → modale Rejoindre/Abandonner
    let aliveM = true;
    const checkActiveMatch = async () => {
      try {
        const raw = sessionStorage.getItem('elementum_active_game');
        if (!raw) return;
        const info = JSON.parse(raw);
        if (!info || !info.gameId) return;
        const onBattle = window.location.pathname.includes('/game/battle') || window.location.hash.includes('/game/battle');
        if (onBattle) return;
        const data = await gameService.getGameState(info.gameId);
        if (!aliveM) return;
        if (data && data.gameState && !data.gameState.isGameOver) {
          setActiveMatch({ gameId: info.gameId });
        } else {
          sessionStorage.removeItem('elementum_active_game');
        }
      } catch (e) { /* partie expirée côté serveur */ }
    };
    checkActiveMatch();
    const onFocusM = () => checkActiveMatch();
    window.addEventListener('focus', onFocusM);
    return () => {
      alive = false;
      clearInterval(timer);
      window.removeEventListener('wallet:refresh', onRefresh);
      aliveM = false;
      window.removeEventListener('focus', onFocusM);
    };
  }, [isAuthenticated, location.pathname]);

  if (isHomePage) {
    return <Outlet />;
  }

  return (
    <div className="main-layout">
      <header className="app-header">
        <div className="header-inner">
          <Link to="/dashboard" className="brand" title="Tableau de bord" aria-label="Tableau de bord">
            <span className="brand-sigil" aria-hidden="true"><span className="brand-sigil-letter">E</span></span>
            <span className="brand-name">ELEMENTUM</span>
          </Link>

          <nav className="main-nav" aria-label="Navigation principale">
            {NAV_LINKS.map((l) => (
              <Link key={l.to} to={l.to} className={`nav-link ${isActive(l.to) ? 'active' : ''}`}>
                <Icon name={l.icon} size={16} />
                <span>{l.label}</span>
              </Link>
            ))}
          </nav>

          <div className="user-actions">
            {isAuthenticated && wallet !== null && (
              <div className="currency-group">
                <span className="header-pill pill-coins" title="Pièces (devnet)">
                  <CoinLogo size={16} />
                  <span className="pill-value">{wallet.coins.toLocaleString('fr-FR')}</span>
                </span>
                <button
                  className="header-pill pill-orin"
                  title="ORIN — jeton de saison · Cliquer pour acheter"
                  onClick={() => setShowOrinBuy(true)}
                >
                  <OrinLogo size={16} />
                  <span className="pill-value">{wallet.orin.toLocaleString('fr-FR')}</span>
                </button>
                <span className="header-pill pill-ori soon" title="ORI — jeton on-chain Solana · Disponible au TGE">
                  <OriLogo size={16} />
                  <span className="pill-value">TGE</span>
                </span>
              </div>
            )}

            <button
              className={`header-wallet-btn ${walletConn ? 'connected' : ''}`}
              onClick={() => setShowWallet(true)}
              title={walletConn ? `Wallet connecté : ${walletConn.address}` : 'Connecter un wallet'}
            >
              <span className={`wallet-dot ${walletConn ? 'on' : ''}`} />
              <Icon name="wallet" size={16} />
              <span className="wallet-label">{walletConn ? shortAddr(walletConn.address) : 'Wallet'}</span>
            </button>

            <Link
              to={isAuthenticated ? '/profile' : '/auth/login'}
              className="user-profile"
              title={isAuthenticated ? 'Profil' : 'Se connecter'}
            >
              {(isAuthenticated && (currentUser?.avatar?.url || currentUser?.companion?.avatarUrl)) ? (
                <img className="user-profile-img" src={currentUser.avatar?.url || currentUser.companion.avatarUrl} alt="Avatar" />
              ) : (
                <Icon name="profile" size={18} />
              )}
            </Link>
          </div>
        </div>
      </header>

      <main className="app-content">

        {/* R49 : achat ORIN on-chain */}
        {showOrinBuy && (
          <Suspense fallback={null}>
            <OrinBuyModal onClose={() => setShowOrinBuy(false)} onNeedWallet={() => { setShowOrinBuy(false); setShowWallet(true); }} />
          </Suspense>
        )}

        {/* Connexion wallet (Phantom/Solflare/Elementum) */}
        {showWallet && (
          <Suspense fallback={null}>
            <WalletModal onClose={() => setShowWallet(false)} />
          </Suspense>
        )}

        {/* R34h : un duel est en cours — Rejoindre / Abandonner */}
        {activeMatch && !location.pathname.includes('/game/battle') && (
          <div className="resume-overlay">
            <div className="resume-modal">
              <div className="resume-emblem" aria-hidden="true">⚔️</div>
              <h3>Un duel est en cours</h3>
              <p>
                Tu as une partie en cours. Reprends-la ou abandonne pour en commencer une autre.
              </p>
              <div className="resume-actions">
                <button
                  className="ui-button primary medium resume-join"
                  onClick={() => { setActiveMatch(null); navigate('/game/battle/' + activeMatch.gameId); }}
                >
                  Rejoindre
                </button>
                <button
                  className="ui-button ghost medium resume-leave"
                  onClick={async () => {
                    const gid = activeMatch.gameId;
                    setActiveMatch(null);
                    try { await gameService.surrenderGame(gid); } catch (e) { /* déjà terminée */ }
                    sessionStorage.removeItem('elementum_active_game');
                    window.location.reload();
                  }}
                >
                  Abandonner
                </button>
              </div>
            </div>
          </div>
        )}
        <Outlet />
      </main>

      <footer className="app-footer">
        <div className="footer-content">
          <span className="footer-brand">Elementum</span>
          <span className="footer-sep">·</span>
          <span>Novarcane Origins</span>
          <span className="footer-sep">·</span>
          <span>© {new Date().getFullYear()}</span>
        </div>
      </footer>
      <TutorialTour autoLaunch={tutorialAutoLaunch} />
    </div>
  );
};

export default MainLayout;
